import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { AlertTriangle, ExternalLink, RefreshCw, XSquare } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Data-quality tab for Reports.
 * Lists fuel cost entries whose values are physically unrealistic and would
 * skew Km/L averages (odometer typos, missing liters, forklift km-vs-hours
 * mistakes, etc.). Each row links to the vehicle's detail page so the main
 * user can fix or delete the entry inline.
 */
export const DataQualityCard = ({ startDate, endDate, monthLabel }) => {
  const [loading, setLoading] = useState(false);
  const [anomalies, setAnomalies] = useState([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start', startDate);
      if (endDate) {
        // API uses `$lt end`, so bump the inclusive end date forward by one day.
        const d = new Date(endDate);
        d.setDate(d.getDate() + 1);
        params.append('end', d.toISOString().slice(0, 10));
      }
      const r = await axios.get(`${API_URL}/api/admin/data-quality/fuel-anomalies?${params.toString()}`);
      setAnomalies(r.data?.anomalies || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to load anomalies';
      setError(msg);
      if (e?.response?.status !== 403) toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  const [excludedVehicles, setExcludedVehicles] = useState([]);

  const loadExcluded = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/vehicles`);
      setExcludedVehicles((r.data || []).filter((v) => v.include_in_fleet_avg === false));
    } catch { /* silent — sub-users hit 403; card hides anyway */ }
  }, []);

  useEffect(() => { load(); loadExcluded(); }, [load, loadExcluded]);

  const excludeVehicle = async (vehicleId, vehicleLabel) => {
    // Un-ticks `include_in_fleet_avg` — the same flag that already drives
    // fleet Km/L exclusion; the Data-Quality endpoint honours it, so future
    // anomalies on this vehicle stop showing up. Confirms before mutating
    // because it also removes the vehicle from fleet-avg calculations.
    if (!window.confirm(
      `Exclude "${vehicleLabel}" from the fleet Km/L average AND the Data Quality nudge?\n\nAll cost history is kept — the vehicle will simply stop being flagged for anomalies and won't affect fleet averages.`
    )) return;
    try {
      await axios.put(`${API_URL}/api/vehicles/${vehicleId}`, { include_in_fleet_avg: false });
      toast.success(`${vehicleLabel} excluded from Data Quality`);
      await load(); await loadExcluded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to exclude vehicle');
    }
  };

  const restoreVehicle = async (vehicle) => {
    // Re-ticks `include_in_fleet_avg` so the vehicle rejoins Km/L averages
    // AND the Data-Quality sweep. Used to undo an accidental exclude.
    try {
      await axios.put(`${API_URL}/api/vehicles/${vehicle.id}`, { include_in_fleet_avg: true });
      toast.success(`${vehicle.name || vehicle.registration} restored`);
      await load(); await loadExcluded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to restore vehicle');
    }
  };

  if (error && error.toLowerCase().includes('main account owner')) {
    return null; // silently hide for sub-users
  }

  return (
    <Card className="card-hover" data-testid="data-quality-card">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-lg font-heading font-semibold flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Data Quality — Fuel Anomalies
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            Entries hidden from Km/L averages because their odometer or liters values are unrealistic ({monthLabel}).
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="reload-anomalies-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </CardHeader>
      {excludedVehicles.length > 0 && (
        <div
          className="px-6 pb-3 -mt-1 flex flex-wrap items-center gap-2 border-b border-slate-100"
          data-testid="excluded-vehicles-bar"
        >
          <span className="text-xs uppercase tracking-wide text-slate-500 mr-1">
            Excluded from Data Quality:
          </span>
          {excludedVehicles.map((v) => (
            <button
              key={v.id}
              type="button"
              onClick={() => restoreVehicle(v)}
              className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-xs bg-slate-100 hover:bg-emerald-100 hover:text-emerald-800 text-slate-600 transition"
              title="Restore to Data Quality & fleet Km/L average"
              data-testid={`restore-vehicle-${v.id}`}
            >
              <span>{v.registration || v.name}</span>
              <span className="ml-0.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-slate-200 group-hover:bg-emerald-200 text-xs">×</span>
            </button>
          ))}
        </div>
      )}
      <CardContent>
        {loading ? (
          <div className="text-slate-400 py-8 text-center">Scanning…</div>
        ) : total === 0 ? (
          <div className="text-slate-400 py-8 text-center" data-testid="no-anomalies">
            No fuel anomalies detected for {monthLabel}. ✓
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-sm text-slate-600">
              <Badge variant="destructive" data-testid="anomaly-count">{total}</Badge> fuel entries need review.
            </div>
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-left">
                  <tr>
                    <th className="px-3 py-2">Date</th>
                    <th className="px-3 py-2">Vehicle</th>
                    <th className="px-3 py-2 text-right">Meter</th>
                    <th className="px-3 py-2 text-right">Liters</th>
                    <th className="px-3 py-2">Reasons</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {anomalies.map((a) => (
                    <tr key={a.id} className="hover:bg-slate-50" data-testid={`anomaly-row-${a.id}`}>
                      <td className="px-3 py-2 whitespace-nowrap">{a.date || '—'}</td>
                      <td className="px-3 py-2">{a.vehicle}</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {a.hours != null ? `${a.hours} hrs` : (a.odometer != null ? `${a.odometer} km` : '—')}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{a.liters ?? '—'}</td>
                      <td className="px-3 py-2 text-amber-700">
                        <ul className="list-disc pl-4 space-y-0.5">
                          {a.reasons.map((r, i) => (<li key={i}>{r}</li>))}
                        </ul>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="flex items-center justify-end gap-3">
                          {a.vehicle_id && (
                            <Link
                              to={`/vehicles/${a.vehicle_id}`}
                              className="text-blue-600 hover:underline inline-flex items-center gap-1"
                              data-testid={`anomaly-fix-link-${a.id}`}
                            >
                              Fix <ExternalLink className="w-3 h-3" />
                            </Link>
                          )}
                          {a.vehicle_id && (
                            <button
                              type="button"
                              onClick={() => excludeVehicle(a.vehicle_id, a.vehicle)}
                              className="text-slate-500 hover:text-red-600 inline-flex items-center gap-1 text-xs"
                              title="Exclude this vehicle from Data Quality nudges and fleet Km/L"
                              data-testid={`anomaly-exclude-btn-${a.id}`}
                            >
                              <XSquare className="w-3.5 h-3.5" />
                              Exclude
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DataQualityCard;
